/**
 * Module-level access-token store. In-memory only — never persisted to
 * localStorage, sessionStorage, or any cookie. Mirrors saas_login's pattern.
 *
 * The decoded JWT payload is cached alongside the token so consumers
 * (useCurrentOperator, route loaders) don't decode on every read.
 */
import { decodeJwtPayload, type JwtPayload } from '@/utils/jwt';

interface StoreState {
  token: string | null;
  expiresAt: string | null;
  claims: JwtPayload | null;
  csrfToken: string | null;
}

const state: StoreState = {
  token: null,
  expiresAt: null,
  claims: null,
  csrfToken: null,
};

let signOutCallback: (() => void) | null = null;

export function getAccessToken(): string | null {
  return state.token;
}

export function getClaims(): JwtPayload | null {
  return state.claims;
}

export function getExpiresAt(): string | null {
  return state.expiresAt;
}

export function setAccessToken(token: string | null, expiresAt: string | null = null): void {
  state.token = token;
  state.expiresAt = expiresAt;
  state.claims = token ? decodeJwtPayload(token) : null;
}

export function getCsrfToken(): string | null {
  return state.csrfToken;
}

/**
 * In-memory CSRF token for `/api/saas-admin/**` mutations. Captured from
 * the refresh response body (`AuthTokenResponse.csrfToken`). NEVER persisted
 * to localStorage/sessionStorage. Cleared by `signOut()`. See specs/0003.
 */
export function setCsrfToken(token: string | null): void {
  state.csrfToken = token;
}

export function registerSignOutCallback(cb: (() => void) | null): void {
  signOutCallback = cb;
}

export function signOut(): void {
  state.token = null;
  state.expiresAt = null;
  state.claims = null;
  state.csrfToken = null;
  signOutCallback?.();
}

/** Test seam. */
export function __resetAuthStore(): void {
  state.token = null;
  state.expiresAt = null;
  state.claims = null;
  state.csrfToken = null;
  signOutCallback = null;
}
